import type { ReactNode } from "react";

import {
  prepareMarkdownTablePresentation,
  type T_TablePresentation,
} from "../chat-v8/table-presentation";
import { segmentMarkdownTables } from "../chat-v8/markdown-table-segments";

/**
 * Pieces each platform supplies. These are FEATURE-level slots, not
 * primitives: "render a markdown block", not "give me a View and a Text".
 * Primitive-level injection would force platform quirks (RN text must be
 * wrapped in <Text>, no CSS cascade, press vs click) into this file.
 */
export type T_PrismTextBodyRenderers = {
  /**
   * Display math pulled out of the prose. Optional: web renderers handle
   * math inline, native needs a dedicated card. When omitted, the raw
   * expression is handed back to renderMarkdown with its delimiters.
   */
  renderMath?: (args: { expression: string; key: string }) => ReactNode;
  /** A markdown block. `isFirst` supports lead/intro typography. */
  renderMarkdown: (args: {
    isFirst: boolean;
    isStreaming: boolean;
    key: string;
    markdown: string;
  }) => ReactNode;
  /** Wraps multiple segments. Single-segment output bypasses this. */
  renderStack: (children: ReactNode[]) => ReactNode;
  /** A table extracted from the prose, already through shared presentation. */
  renderTable: (args: {
    key: string;
    presentation: T_TablePresentation;
  }) => ReactNode;
};

export type T_PrismTextBodyProps = {
  /** True while this part is still streaming (item.isLive). */
  isStreaming?: boolean;
  markdown: string;
  renderers: T_PrismTextBodyRenderers;
};

/**
 * Assistant reply body composition, shared by web and native.
 *
 * This owns the RECIPE — segmentation, streaming rules, branch selection,
 * ordering, and table preparation — while each platform owns the PIECES.
 * Both platforms previously duplicated this recipe, which is how they drifted
 * into rendering markdown tables two different ways in one answer: native
 * segmented pipe tables into the table card, web handed raw markdown to its
 * markdown renderer and got that renderer's own table chrome.
 */
export function PrismTextBody({
  isStreaming = false,
  markdown,
  renderers,
}: T_PrismTextBodyProps): ReactNode {
  const segments = segmentMarkdownTables(markdown, {
    streamEnded: !isStreaming,
  });

  const firstSegment = segments[0];
  if (segments.length === 1 && firstSegment?.kind === "text") {
    return renderers.renderMarkdown({
      isFirst: true,
      isStreaming,
      key: "text-0",
      markdown: firstSegment.markdown,
    });
  }

  let renderedTextSegments = 0;
  const children = segments.map((segment, index) => {
    if (segment.kind === "table") {
      return renderers.renderTable({
        key: `table-${index}`,
        presentation: prepareMarkdownTablePresentation(segment.table),
      });
    }

    if (segment.kind === "math") {
      if (renderers.renderMath) {
        return renderers.renderMath({
          expression: segment.expression,
          key: `math-${index}`,
        });
      }
      // No math slot: hand the block back as markdown with its delimiters
      // restored, so a platform whose markdown renderer supports math keeps
      // working without implementing a second path.
      return renderers.renderMarkdown({
        isFirst: false,
        isStreaming,
        key: `math-${index}`,
        markdown: `$$\n${segment.expression}\n$$`,
      });
    }

    const isFirst = renderedTextSegments === 0;
    renderedTextSegments += 1;
    return renderers.renderMarkdown({
      isFirst,
      isStreaming,
      key: `text-${index}`,
      markdown: segment.markdown,
    });
  });

  return renderers.renderStack(children);
}
